"use client";

import { useForm } from "react-hook-form";
import type { Resolver, FieldErrors } from "react-hook-form";
import * as z from "zod";
import { useTranslations } from "next-intl";
import { useMemo } from "react";
import { useReCaptcha } from "next-recaptcha-v3";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import FileUpload from "@/components/ui/FileUpload";
import { sendCareerEmail } from "@/actions/email";
import { useState } from "react";

const recaptchaSiteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY ?? "";

type FormData = {
	name: string;
	contactMethod: "email" | "phone";
	email: string;
	phone?: string;
	jobType: string[];
	workType: string[];
	skills: string;
};

function CareersFormBase({
	getToken,
}: {
	getToken: (() => Promise<string>) | null;
}) {
	const t = useTranslations("CareersForm");

	const schema = useMemo(
		() =>
			z
				.object({
					name: z.string().min(2, t("nameRequired")),
					contactMethod: z.enum(["email", "phone"]),
					email: z.string().email().optional().or(z.literal("")),
					phone: z.string().optional(),
					jobType: z.array(z.string()).min(1, t("jobTypeRequired")),
					workType: z.array(z.string()).min(1, t("workTypeRequired")),
					skills: z.string().min(2, t("skillsRequired")),
				})
				.refine(
					(data) => {
						if (data.contactMethod === "email" && !data.email) return false;
						if (data.contactMethod === "phone" && !data.phone) return false;
						return true;
					},
					{ message: t("contactDetailsRequired"), path: ["email"] },
				),
		[t],
	);

	const customZodResolver: Resolver<FormData> = useMemo(
		() => (values) => {
			const result = schema.safeParse(values);
			if (result.success) {
				return { values: result.data as FormData, errors: {} };
			}
			const errors: FieldErrors<FormData> = {};
			for (const issue of result.error.issues) {
				const path = issue.path[0] as keyof FormData;
				if (path && !errors[path]) {
					errors[path] = { type: "validation", message: issue.message };
				}
			}
			return { values: {} as Record<string, never>, errors };
		},
		[schema],
	);

	const [status, setStatus] = useState<
		"idle" | "submitting" | "success" | "error"
	>("idle");
	const [files, setFiles] = useState<File[]>([]);
	const [recaptchaError, setRecaptchaError] = useState<string | null>(null);

	const {
		register,
		handleSubmit,
		watch,
		setValue,
		formState: { errors, isValid },
		reset,
	} = useForm<FormData>({
		resolver: customZodResolver,
		mode: "onChange",
		defaultValues: {
			contactMethod: "phone",
			jobType: [],
			workType: [],
		},
	});

	const contactMethod = watch("contactMethod");

	const onSubmit = async (data: FormData) => {
		if (files.length === 0) {
			alert(t("resumeAlert"));
			return;
		}
		setStatus("submitting");
		setRecaptchaError(null);
		let token: string | null = null;
		if (getToken) {
			try {
				token = await getToken();
			} catch {
				setRecaptchaError(t("recaptchaError"));
				setStatus("idle");
				return;
			}
		}
		const formData = new FormData();
		formData.append("name", data.name);
		if (data.email) formData.append("email", data.email);
		if (data.phone) formData.append("phone", data.phone);
		formData.append("job_type", data.jobType.join(", "));
		formData.append("work_type", data.workType.join(", "));
		formData.append("skills", data.skills);
		if (token) formData.append("recaptchaToken", token);

		files.forEach((file) => {
			formData.append("resume", file);
		});

		const result = await sendCareerEmail(formData);
		if (result.success) {
			setStatus("success");
			reset();
			setFiles([]);
		} else {
			setStatus("error");
		}
	};

	const handleCheckboxChange = (
		field: "jobType" | "workType",
		value: string,
	) => {
		const current = watch(field) || [];
		const updated = current.includes(value)
			? current.filter((item) => item !== value)
			: [...current, value];
		setValue(field, updated);
	};

	return (
		<form
			onSubmit={handleSubmit(onSubmit)}
			className="space-y-6 bg-white dark:bg-gray-800 p-8 rounded-lg shadow-md"
		>
			<div>
				<Input
					{...register("name")}
					placeholder={t("namePlaceholder")}
					className={errors.name ? "border-red-500" : ""}
				/>
				{errors.name && (
					<p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
				)}
			</div>

			<div className="flex items-center space-x-4">
				<label className="flex items-center space-x-2">
					<input
						type="radio"
						value="phone"
						{...register("contactMethod")}
						className="text-primary focus:ring-primary"
					/>
					<span className="dark:text-white">{t("contactPhone")}</span>
				</label>
				<label className="flex items-center space-x-2">
					<input
						type="radio"
						value="email"
						{...register("contactMethod")}
						className="text-primary focus:ring-primary"
					/>
					<span className="dark:text-white">{t("contactEmail")}</span>
				</label>
			</div>

			{contactMethod === "email" ? (
				<div>
					<Input
						{...register("email")}
						placeholder={t("emailPlaceholder")}
						type="email"
						className={errors.email ? "border-red-500" : ""}
					/>
					{errors.email && (
						<p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
					)}
				</div>
			) : (
				<div>
					<Input
						{...register("phone")}
						placeholder={t("phonePlaceholder")}
						type="tel"
						className={errors.phone ? "border-red-500" : ""}
					/>
					{errors.phone && (
						<p className="text-red-500 text-sm mt-1">{errors.phone.message}</p>
					)}
				</div>
			)}

			<div>
				<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
					{t("jobTypeLabel")}
				</label>
				<div className="flex flex-wrap gap-2">
					{["Full Time", "Part Time", "Project Base"].map((type) => (
						<button
							key={type}
							type="button"
							onClick={() => handleCheckboxChange("jobType", type)}
							className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
								watch("jobType")?.includes(type)
									? "bg-primary text-white"
									: "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300"
							}`}
						>
							{type}
						</button>
					))}
				</div>
				{errors.jobType && (
					<p className="text-red-500 text-sm mt-1">{errors.jobType.message}</p>
				)}
			</div>

			<div>
				<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
					{t("workTypeLabel")}
				</label>
				<div className="flex flex-wrap gap-2">
					{["Remote", "Hybrid", "Office"].map((type) => (
						<button
							key={type}
							type="button"
							onClick={() => handleCheckboxChange("workType", type)}
							className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
								watch("workType")?.includes(type)
									? "bg-primary text-white"
									: "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300"
							}`}
						>
							{type}
						</button>
					))}
				</div>
				{errors.workType && (
					<p className="text-red-500 text-sm mt-1">{errors.workType.message}</p>
				)}
			</div>

			<div>
				<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
					What have you used in you career?
				</label>
				<Input
					{...register("skills")}
					placeholder="e.g. Angular, React, Node.js (comma separated)"
					className={errors.skills ? "border-red-500" : ""}
				/>
				{errors.skills && (
					<p className="text-red-500 text-sm mt-1">{errors.skills.message}</p>
				)}
			</div>

			<FileUpload files={files} onFilesChange={setFiles} />

			{recaptchaError && (
				<p className="text-red-500 text-sm mt-1">{recaptchaError}</p>
			)}

			<Button
				type="submit"
				size="xl"
				disabled={
					status === "submitting" || !isValid || files.length === 0
				}
				loading={status === "submitting"}
				className="w-full"
			>
				{status === "submitting" ? t("sending") : t("button")}
			</Button>

			{status === "success" && (
				<p className="text-green-600 text-center">{t("success")}</p>
			)}
			{status === "error" && (
				<p className="text-red-600 text-center">{t("error")}</p>
			)}
		</form>
	);
}

function CareersFormWithRecaptcha() {
	const { executeRecaptcha } = useReCaptcha();
	const getToken = () => executeRecaptcha("careers_submit");
	return <CareersFormBase getToken={getToken} />;
}

export default function CareersForm() {
	if (!recaptchaSiteKey) return <CareersFormBase getToken={null} />;
	return <CareersFormWithRecaptcha />;
}
